import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Tabs, message, Card, Alert, Modal, Divider, Space } from 'antd';
import { UserOutlined, LockOutlined, PhoneOutlined, KeyOutlined, WechatOutlined, MessageOutlined } from '@ant-design/icons';
import { useAuthStore } from '../stores/authStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AgreementConsent, { requireAgreement } from '../components/AgreementConsent';
import axios from 'axios';

// 配置axios基础URL，指向后端服务
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';
const { TabPane } = Tabs;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [wechatLoading, setWechatLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [loginError, setLoginError] = useState('');
  const [forgotPasswordVisible, setForgotPasswordVisible] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordForm] = Form.useForm();
  const [smsLoginForm] = Form.useForm();
  const [smsRegisterForm] = Form.useForm();
  const { login, register, verifyToken } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referrerCode = searchParams.get('ref'); // 获取推荐码
  const [agreedPolicy, setAgreedPolicy] = useState(false);
  
  // 检测是否在APP的WebView内
  const [isInAppWebView, setIsInAppWebView] = useState(false);

  // 检测是否在小程序内（优先使用官方 getEnv）
  // 注意：普通 PC 浏览器里 jweixin 也提供 getEnv，但其回调不会触发，
  // 若无超时兜底，await 会永远卡住 → PC 微信登录一直转圈、二维码出不来。
  const detectMiniProgramEnv = () => {
    return new Promise((resolve) => {
      let done = false;
      const fallback = (typeof window !== 'undefined' && window.__wxjs_environment === 'miniprogram');
      const finish = (v) => { if (!done) { done = true; resolve(v); } };
      const timer = setTimeout(() => finish(fallback), 600); // 600ms 兜底，绝不卡死
      try {
        if (window.wx && window.wx.miniProgram && typeof window.wx.miniProgram.getEnv === 'function') {
          window.wx.miniProgram.getEnv((res) => { clearTimeout(timer); finish(!!res?.miniprogram); });
          return;
        }
      } catch (_) {}
      clearTimeout(timer);
      finish(fallback);
    });
  };

  useEffect(() => {
    // 页面加载时验证token
    verifyToken();
    
    // 检测是否在APP的WebView内
    // 通过User Agent检测（最可靠）
    const userAgent = navigator.userAgent || '';
    const isWebView = userAgent.includes('ManagementMasterApp');
    
    if (isWebView) {
      setIsInAppWebView(true);
      console.log('✅ 检测到APP WebView环境, UserAgent:', userAgent);
    } else {
      setIsInAppWebView(false);
      console.log('普通浏览器环境, UserAgent:', userAgent);
    }
    
    // 如果URL中有推荐码，保存到localStorage
    if (referrerCode) {
      localStorage.setItem('referrerCode', referrerCode);
      message.info('检测到推荐码，请注册账号！');
      setActiveTab('sms-register');
    }
  }, [verifyToken, referrerCode]);

  const handleLogin = async (values) => {
    if (!requireAgreement(agreedPolicy)) return;
    setLoading(true);
    setLoginError('');
    try {
      const result = await login(values.phone, values.password);
      if (result.success) {
        message.success('登录成功');
        // 根据用户角色自动跳转
        setTimeout(() => {
          // 管理员与普通用户一致：登录后统一进首页（管理后台入口在「我的」里）
          navigate('/');
        }, 1000);
      } else {
        setLoginError(result.message);
      }
    } catch (error) {
      setLoginError('登录失败，请检查网络连接');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (values) => {
    if (!requireAgreement(agreedPolicy)) return;
    setLoading(true);
    try {
      // 从URL或localStorage获取推荐码
      const savedReferrerCode = referrerCode || localStorage.getItem('referrerCode');
      const result = await register(values.phone, values.password, savedReferrerCode);
      if (result.success) {
        message.success('注册成功');
        // 清除localStorage中的推荐码
        localStorage.removeItem('referrerCode');
        // 根据用户角色自动跳转
        setTimeout(() => {
          // 管理员与普通用户一致：登录后统一进首页（管理后台入口在「我的」里）
          navigate('/');
        }, 1000);
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('注册失败');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (values) => {
    setForgotPasswordLoading(true);
    try {
      const response = await axios.post('/auth/forgot-password', {
        phone: values.phone,
        lastFourDigits: values.lastFourDigits
      });
      
      if (response.data.success) {
        message.success(`密码重置成功！新密码是：${response.data.newPassword}`);
        setForgotPasswordVisible(false);
        forgotPasswordForm.resetFields();
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      message.error('密码找回失败，请检查输入信息');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleWeChatLogin = async () => {
    if (!requireAgreement(agreedPolicy)) return;
    console.log('🚀 微信登录被点击');
    console.log('🔍 isInAppWebView状态:', isInAppWebView);
    console.log('🔍 UserAgent:', navigator.userAgent);
    
    // 2. 先检测APP环境（最优先）
    if (isInAppWebView) {
      console.log('✅ 检测到APP环境，使用postMessage通信');
      setWechatLoading(true);
      
      try {
        // 优先使用postMessage（更可靠）
        if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
          console.log('📤 方式1: 使用postMessage');
          window.ReactNativeWebView.postMessage(JSON.stringify({
            type: 'WECHAT_LOGIN'
          }));
          console.log('✅ postMessage已发送');
          message.success('正在唤起微信...', 2);
        } else {
          console.log('⚠️ ReactNativeWebView不可用，尝试URL方式');
          // 备用方式：URL跳转
          console.log('📤 方式2: window.location.href');
          window.location.href = 'weixin://login';
          message.success('正在唤起微信...', 2);
        }
        
        // 5秒后重置loading
        setTimeout(() => {
          console.log('⏰ 5秒超时，重置loading状态');
          setWechatLoading(false);
        }, 5000);
      } catch (error) {
        console.error('❌ 调起微信失败:', error);
        setWechatLoading(false);
        message.error('调起微信失败: ' + error.message);
      }
      
      return;
    }
    
    setWechatLoading(true);
    
    try {
      // 1. 检测是否在小程序环境
      const inMiniProgram = await detectMiniProgramEnv();
      console.log('小程序检测结果:', inMiniProgram);

      if (inMiniProgram) {
        if (window.wx && window.wx.miniProgram) {
          // 先通知小程序（可选）
          if (typeof window.wx.miniProgram.postMessage === 'function') {
            window.wx.miniProgram.postMessage({ data: { type: 'wechat_login_request' } });
          }
          // 直接跳转到小程序登录页，调用小程序的微信登录
          if (typeof window.wx.miniProgram.navigateTo === 'function') {
            window.wx.miniProgram.navigateTo({ url: '/pages/login/login' });
          }
        }
        return;
      }

      // 3. 普通浏览器环境：跳转至网页微信授权/扫码
      const response = await axios.get('/auth/wechat/auth-url');
      if (response.data.success && response.data.authUrl) {
        window.location.href = response.data.authUrl;
      } else {
        message.error(response.data.message || '获取微信登录链接失败');
      }
    } catch (error) {
      console.error('微信登录失败:', error);
      message.error('微信登录失败，请稍后重试');
    } finally {
      setWechatLoading(false);
    }
  };

  const handleSMSLogin = async (values) => {
    if (!requireAgreement(agreedPolicy)) return;
    setLoading(true);
    setLoginError(''); // 清除之前的错误信息
    try {
      const response = await axios.post('/auth/sms/login', {
        phone: values.phone,
        sms_code: values.sms_code
      });
      
      if (response.data.success) {
        message.success('短信登录成功');
        
        // 直接处理短信登录成功后的状态
        const { token, user } = response.data;
        
        // 保存到localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userId', user.id);
        localStorage.setItem('userRole', user.role);
        
        // 设置axios默认headers
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // 更新authStore状态
        useAuthStore.setState({
          user,
          token,
          isAuthenticated: true
        });
        
        // 跳转到相应页面：管理员与普通用户一致，统一进首页
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        message.error(response.data.message);
        setLoginError(response.data.message);
      }
    } catch (error) {
      console.error('短信登录错误:', error);
      const errorMessage = error.response?.data?.message || '短信登录失败，请检查验证码';
      message.error(errorMessage);
      setLoginError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSMSRegister = async (values) => {
    if (!requireAgreement(agreedPolicy)) return;
    setLoading(true);
    try {
      // 从URL或localStorage获取推荐码
      const savedReferrerCode = referrerCode || localStorage.getItem('referrerCode');
      const response = await axios.post('/auth/sms/register', {
        phone: values.phone,
        sms_code: values.sms_code,
        nickname: values.nickname,
        password: values.password,
        referrerCode: savedReferrerCode
      });
      
      if (response.data.success) {
        message.success('短信注册成功');
        
        // 清除localStorage中的推荐码
        localStorage.removeItem('referrerCode');
        
        // 直接处理短信注册成功后的状态
        const { token, user } = response.data;
        
        // 保存到localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userId', user.id);
        localStorage.setItem('userRole', user.role);
        
        // 设置axios默认headers
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // 更新authStore状态
        useAuthStore.setState({
          user,
          token,
          isAuthenticated: true
        });
        
        // 统一进首页
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      console.error('短信注册错误:', error);
      const errorMessage = error.response?.data?.message || '短信注册失败，请检查验证码';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const sendSMSCode = async (phone, purpose = 'login') => {
    try {
      const response = await axios.post('/auth/sms/send-code', {
        phone,
        purpose
      });
      
      if (response.data.success) {
        message.success(response.data.message);
        return true;
      } else {
        message.error(response.data.message);
        return false;
      }
    } catch (error) {
      message.error('发送验证码失败，请重试');
      return false;
    }
  };

  return (
    <div className="login-container">
      <Card className="login-form">
        <div className="login-title">管理师</div>

        <AgreementConsent checked={agreedPolicy} onChange={setAgreedPolicy} />

        <Tabs activeKey={activeTab} onChange={setActiveTab} centered>
          <TabPane tab="密码登录" key="login">
            {loginError && (
              <Alert
                message={loginError}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                action={
                  <Button size="small" type="link" onClick={() => setActiveTab('register')}>
                    现在注册
                  </Button>
                }
              />
            )}
            <Form
              name="login"
              onFinish={handleLogin}
              autoComplete="off"
              size="large"
            >
              <Form.Item
                name="phone"
                rules={[{ required: true, message: '请输入手机号' }]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="手机号"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: '请输入密码' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="密码"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                >
                  登录
                </Button>
              </Form.Item>

              {/* 微信登录按钮 - 根据环境自动选择登录方式 */}
              <Form.Item>
                <Button
                  icon={<WechatOutlined />}
                  onClick={handleWeChatLogin}
                  loading={wechatLoading}
                  block
                  style={{ 
                    backgroundColor: '#07C160', 
                    borderColor: '#07C160',
                    color: 'white'
                  }}
                >
                  微信登录
                </Button>
              </Form.Item>
              
              <Form.Item style={{ textAlign: 'center', marginBottom: 0 }}>
                <Button
                  type="link"
                  icon={<KeyOutlined />}
                  onClick={() => setForgotPasswordVisible(true)}
                >
                  忘记密码？
                </Button>
              </Form.Item>
              
              <Form.Item style={{ textAlign: 'center', marginBottom: 0 }}>
                <Space>
                  <Button
                    type="link"
                    icon={<MessageOutlined />}
                    onClick={() => setActiveTab('sms-register')}
                  >
                    短信注册
                  </Button>
                  <Button
                    type="link"
                    icon={<UserOutlined />}
                    onClick={() => setActiveTab('register')}
                  >
                    密码注册
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="短信登录" key="sms-login">
            <Form
              form={smsLoginForm}
              name="sms-login"
              onFinish={handleSMSLogin}
              autoComplete="off"
              size="large"
            >
              <Form.Item
                name="phone"
                rules={[
                  { required: true, message: '请输入手机号' },
                  { pattern: /^1\d{10}$/, message: '请输入正确的11位手机号' }
                ]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="手机号"
                />
              </Form.Item>

              <Form.Item
                name="sms_code"
                rules={[
                  { required: true, message: '请输入验证码' },
                  { pattern: /^\d{6}$/, message: '验证码必须是6位数字' }
                ]}
              >
                <Input
                  prefix={<MessageOutlined />}
                  placeholder="短信验证码"
                  maxLength={6}
                  suffix={
                    <Button 
                      type="link" 
                      onClick={async () => {
                        const raw = smsLoginForm.getFieldValue('phone');
                        const phone = (raw || '').toString().replace(/\s+/g, '');
                        if (phone && /^1\d{10}$/.test(phone)) {
                          await sendSMSCode(phone, 'login');
                        } else {
                          message.error('请先输入正确的手机号');
                        }
                      }}
                    >
                      发送验证码
                    </Button>
                  }
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                >
                  短信登录
                </Button>
              </Form.Item>

              {/* 微信登录按钮 - 根据环境自动选择登录方式 */}
              <Form.Item>
                <Button
                  icon={<WechatOutlined />}
                  onClick={handleWeChatLogin}
                  loading={wechatLoading}
                  block
                  style={{ 
                    backgroundColor: '#07C160', 
                    borderColor: '#07C160',
                    color: 'white'
                  }}
                >
                  微信登录
                </Button>
              </Form.Item>
              
              <Form.Item style={{ textAlign: 'center', marginBottom: 0 }}>
                <Button
                  type="link"
                  icon={<MessageOutlined />}
                  onClick={() => setActiveTab('sms-register')}
                >
                  短信注册
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="密码注册" key="register">
            <Form
              name="register"
              onFinish={handleRegister}
              autoComplete="off"
              size="large"
            >
              <Form.Item
                name="phone"
                rules={[
                  { required: true, message: '请输入手机号' },
                  { pattern: /^1\d{10}$/, message: '请输入正确的11位手机号' }
                ]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="手机号"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 6, message: '密码至少6位' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="密码"
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  { required: true, message: '请确认密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不一致'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="确认密码"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                >
                  注册
                </Button>
              </Form.Item>

              {/* 微信登录按钮 - 根据环境自动选择登录方式 */}
              <Form.Item>
                <Button
                  icon={<WechatOutlined />}
                  onClick={handleWeChatLogin}
                  loading={wechatLoading}
                  block
                  style={{ 
                    backgroundColor: '#07C160', 
                    borderColor: '#07C160',
                    color: 'white'
                  }}
                >
                  微信登录
                </Button>
              </Form.Item>
              
              <Form.Item style={{ textAlign: 'center', marginBottom: 0 }}>
                <Button
                  type="link"
                  icon={<UserOutlined />}
                  onClick={() => setActiveTab('login')}
                >
                  已有账号？立即登录
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="短信注册" key="sms-register">
            <Form
              form={smsRegisterForm}
              name="sms-register"
              onFinish={handleSMSRegister}
              autoComplete="off"
              size="large"
            >
              <Form.Item
                name="phone"
                rules={[
                  { required: true, message: '请输入手机号' },
                  { pattern: /^1\d{10}$/, message: '请输入正确的11位手机号' }
                ]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="手机号"
                />
              </Form.Item>

              <Form.Item
                name="nickname"
                rules={[
                  { required: true, message: '请输入昵称' },
                  { min: 2, message: '昵称至少2位' }
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="昵称"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 6, message: '密码至少6位' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="密码"
                />
              </Form.Item>

              <Form.Item
                name="sms_code"
                rules={[
                  { required: true, message: '请输入验证码' },
                  { pattern: /^\d{6}$/, message: '验证码必须是6位数字' }
                ]}
              >
                <Input
                  prefix={<MessageOutlined />}
                  placeholder="短信验证码"
                  maxLength={6}
                  suffix={
                    <Button 
                      type="link" 
                      onClick={async () => {
                        const raw = smsRegisterForm.getFieldValue('phone');
                        const phone = (raw || '').toString().replace(/\s+/g, '');
                        if (phone && /^1\d{10}$/.test(phone)) {
                          await sendSMSCode(phone, 'register');
                        } else {
                          message.error('请先输入正确的手机号');
                        }
                      }}
                    >
                      发送验证码
                    </Button>
                  }
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                >
                  短信注册
                </Button>
              </Form.Item>

              {/* 微信登录按钮 - 根据环境自动选择登录方式 */}
              <Form.Item>
                <Button
                  icon={<WechatOutlined />}
                  onClick={handleWeChatLogin}
                  loading={wechatLoading}
                  block
                  style={{ 
                    backgroundColor: '#07C160', 
                    borderColor: '#07C160',
                    color: 'white'
                  }}
                >
                  微信登录
                </Button>
              </Form.Item>
              
              <Form.Item style={{ textAlign: 'center', marginBottom: 0 }}>
                <Button
                  type="link"
                  icon={<UserOutlined />}
                  onClick={() => setActiveTab('login')}
                >
                  已有账号？立即登录
                </Button>
              </Form.Item>
            </Form>
          </TabPane>
        </Tabs>

        <div style={{ textAlign: 'center', marginTop: '20px', color: '#666' }}>
          <p>支持手机号密码登录、短信验证码登录和微信快速登录</p>
        </div>
      </Card>

      {/* 密码找回模态框 */}
      <Modal
        title="密码找回"
        open={forgotPasswordVisible}
        onCancel={() => {
          setForgotPasswordVisible(false);
          forgotPasswordForm.resetFields();
        }}
        footer={null}
        width={400}
      >
        <Form
          form={forgotPasswordForm}
          onFinish={handleForgotPassword}
          layout="vertical"
        >
          <Form.Item
            name="phone"
            label="手机号"
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1\d{10}$/, message: '请输入正确的11位手机号' }
            ]}
          >
            <Input
              prefix={<PhoneOutlined />}
              placeholder="请输入手机号"
            />
          </Form.Item>

          <Form.Item
            name="lastFourDigits"
            label="手机号后四位"
            rules={[
              { required: true, message: '请输入手机号后四位' },
              { pattern: /^\d{4}$/, message: '请输入4位数字' }
            ]}
          >
            <Input
              prefix={<KeyOutlined />}
              placeholder="请输入手机号后四位"
              maxLength={4}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={forgotPasswordLoading}
              block
            >
              重置密码
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Login;
